// Champion definitions. Base stats here; level scaling applied at runtime
// via UnitStats.effectiveStats(). abilityCooldown is turns between ability casts;
// unit fires as soon as cooldownTurns reaches 0.

export type ChampionTier = "common" | "rare" | "epic" | "legendary";

export type ChampionRegion =
  | "freljord"
  | "demacia"
  | "noxus"
  | "piltover"
  | "ixtal"
  | "shurima"
  | "targon";

export type ChampionId =
  // Unregioned (legacy pool — recruitable but not regional enemies)
  | "Ahri"
  | "Alistar"
  | "Chogath"
  | "Illaoi"
  | "KhaZix"
  | "Leona"
  | "MasterYi"
  | "Olaf"
  | "Karthus"
  | "Jax"
  | "Mundo"
  | "Lux"
  | "Yorick"
  | "KaiSa"
  | "Lissandra"
  | "Viktor"
  | "Fiora"
  | "Malphite"
  | "Nidalee"
  | "Neeko"
  // Freljord
  | "Tryndamere"
  | "Ashe"
  // Demacia
  | "Garen"
  | "Ezreal"
  | "Sylas"
  // Noxus
  | "Darius"
  | "Katarina"
  | "Vladimir"
  | "Draven"
  // Piltover & Zaun
  | "Vayne"
  | "Vi"
  | "Jinx"
  | "Warwick"
  | "Caitlyn"
  | "Jayce"
  // Shurima
  | "Renekton"
  | "Nasus"
  | "Sivir"
  | "Xerath"
  | "Azir"
  // Targon
  | "Taric"
  | "Pantheon"
  | "Zoe"
  | "Soraka"
  | "AurelionSol"
  | "Yuumi";

export type AbilityId =
  | "ahriOrb"
  | "alistarPulverize"
  | "ezrealMysticShot"
  | "garenJudgment"
  | "illaoiTentacle"
  | "jarvanCataclysm"
  | "khazixLeap"
  | "leonaShield"
  | "masterYiAlphaStrike"
  | "olafRagnarok"
  | "blitzcrankHook"
  | "blitzcrankPunch"
  | "katarinaDeathLotus"
  | "karthusRequiem"
  | "asheVolley"
  | "chogathRupture"
  | "jaxCounterStrike"
  | "mundoUlt"
  | "sorakaWish"
  | "taricDazzle"
  | "luxFinalSpark"
  | "yorickSummonGhoul"
  | "kaisaUlt"
  | "nasusSiphoning"
  | "jinxFishbones"
  | "lissandraIceTomb"
  | "viktorDeathRay"
  | "fioraRiposte"
  | "malphiteUltimate"
  // New abilities
  | "tryndamereBloodlust"
  | "sylasChains"
  | "dariusApprehend"
  | "vladimirTransfusion"
  | "dravenSpinning"
  | "viVaultBreaker"
  | "caitlynPeacemaker"
  | "jayceMercuryHammer"
  | "renektonDominus"
  | "sivirRicochet"
  | "xerathArcaneBarrage"
  | "azirSoldier"
  | "pantheonShield"
  | "zoeSleepyTrouble"
  | "aurelionStarfire"
  | "yuumiHeal"
  | "nidaleeJavelin"
  | "neekoBounceQ";

export const TIER_COLORS: Record<ChampionTier, string> = {
  common: "#7a8693",
  rare: "#22c55e",
  epic: "#c084fc",
  legendary: "#fbbf24",
};

export interface TierStyle {
  borderPx: number;
  glowBlur: number;
  glowAlphaHex: string;
}

export const TIER_STYLES: Record<ChampionTier, TierStyle> = {
  common:    { borderPx: 2, glowBlur: 0,  glowAlphaHex: "00" },
  rare:      { borderPx: 3, glowBlur: 18, glowAlphaHex: "bb" },
  epic:      { borderPx: 4, glowBlur: 30, glowAlphaHex: "ee" },
  legendary: { borderPx: 5, glowBlur: 42, glowAlphaHex: "ff" },
};

export interface LevelGrowth {
  hp: number;          // multiplier added per level (e.g. 0.15 = +15% base HP per level)
  ad: number;
  ap: number;
  attackSpeed: number; // multiplier per level applied to baseAttackSpeed
}

export interface ChampionDef {
  id: ChampionId;
  displayName: string;
  tier: ChampionTier;
  region?: ChampionRegion;
  baseHp: number;
  baseAd: number;
  baseAp: number;
  range: number;
  moveSpeed: number;
  baseAttackSpeed: number; // attacks per second
  abilityId: AbilityId | null;
  abilityCooldown: number; // turns between ability casts
  iconId: string;
  levelGrowth: LevelGrowth;
  lifestealPercent?: number;   // fraction of auto-attack damage healed back
  bonusAutoEvery?: number;     // deal 150% damage every Nth auto
  critChance?: number;         // fraction of autos that crit (1.75× damage)
  yuumiAura?: boolean;         // grants front ally +AD/AP/HP scaling with Yuumi's stars (1★ +10%, 2★ +15%, 3★ +20%)
}

export const CHAMPIONS: Record<ChampionId, ChampionDef> = {
  // ── Unregioned legacy pool ──────────────────────────────────────────────────
  Ahri: {
    id: "Ahri",
    displayName: "Ahri",
    tier: "common",
    baseHp: 340, baseAd: 36, baseAp: 42,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "ahriOrb", abilityCooldown: 2,
    iconId: "Ahri",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  Alistar: {
    id: "Alistar",
    displayName: "Alistar",
    tier: "common",
    baseHp: 950, baseAd: 90, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "alistarPulverize", abilityCooldown: 3,
    iconId: "Alistar",
    levelGrowth: { hp: 0.5294, ad: 0.1176, ap: 0.1176, attackSpeed: 0 },
  },
  Chogath: {
    id: "Chogath",
    displayName: "Cho'Gath",
    tier: "rare",
    baseHp: 1100, baseAd: 82, baseAp: 127,
    range: 1, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "chogathRupture", abilityCooldown: 5,
    iconId: "Chogath",
    levelGrowth: { hp: 0.35, ad: 0.1176, ap: 0.2353, attackSpeed: 0 },
  },
  Illaoi: {
    id: "Illaoi",
    displayName: "Illaoi",
    tier: "common",
    baseHp: 900, baseAd: 21, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "illaoiTentacle", abilityCooldown: 2,
    iconId: "Illaoi",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  KhaZix: {
    id: "KhaZix",
    displayName: "Kha'Zix",
    tier: "common",
    baseHp: 420, baseAd: 127, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "khazixLeap", abilityCooldown: 2,
    iconId: "Khazix",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  Leona: {
    id: "Leona",
    displayName: "Leona",
    tier: "common",
    baseHp: 700, baseAd: 82, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "leonaShield", abilityCooldown: 2,
    iconId: "Leona",
    levelGrowth: { hp: 0.5294, ad: 0.1176, ap: 0.1176, attackSpeed: 0 },
  },
  MasterYi: {
    id: "MasterYi",
    displayName: "Master Yi",
    tier: "epic",
    baseHp: 540, baseAd: 210, baseAp: 0,
    range: 1, moveSpeed: 3, baseAttackSpeed: 1,
    abilityId: "masterYiAlphaStrike", abilityCooldown: 4,
    iconId: "MasterYi",
    levelGrowth: { hp: 0.2941, ad: 0.2353, ap: 0, attackSpeed: 0 },
    lifestealPercent: 0,
  },
  Olaf: {
    id: "Olaf",
    displayName: "Olaf",
    tier: "rare",
    baseHp: 670, baseAd: 270, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "olafRagnarok", abilityCooldown: 4,
    iconId: "Olaf",
    levelGrowth: { hp: 0.2941, ad: 0.1765, ap: 0, attackSpeed: 0 },
    lifestealPercent: 0.12,
  },
  Karthus: {
    id: "Karthus",
    displayName: "Karthus",
    tier: "legendary",
    baseHp: 320, baseAd: 45, baseAp: 214,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "karthusRequiem", abilityCooldown: 4,
    iconId: "Karthus",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  Jax: {
    id: "Jax",
    displayName: "Jax",
    tier: "common",
    baseHp: 1100, baseAd: 162, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "jaxCounterStrike", abilityCooldown: 3,
    iconId: "Jax",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Mundo: {
    id: "Mundo",
    displayName: "Dr. Mundo",
    tier: "epic",
    region: "piltover",
    baseHp: 1200, baseAd: 133, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "mundoUlt", abilityCooldown: 3,
    iconId: "DrMundo",
    levelGrowth: { hp: 0.2941, ad: 0.1765, ap: 0, attackSpeed: 0 },
  },
  Lux: {
    id: "Lux",
    displayName: "Lux",
    tier: "epic",
    region: "demacia",
    baseHp: 360, baseAd: 38, baseAp: 169,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "luxFinalSpark", abilityCooldown: 4,
    iconId: "Lux",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.3529, attackSpeed: 0 },
  },
  Yorick: {
    id: "Yorick",
    displayName: "Yorick",
    tier: "legendary",
    baseHp: 820, baseAd: 103, baseAp: 0,
    range: 1, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "yorickSummonGhoul", abilityCooldown: 1,
    iconId: "Yorick",
    levelGrowth: { hp: 0.4118, ad: 0.1176, ap: 0, attackSpeed: 0 },
  },
  KaiSa: {
    id: "KaiSa",
    displayName: "Kai'Sa",
    tier: "legendary",
    baseHp: 520, baseAd: 112, baseAp: 107,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "kaisaUlt", abilityCooldown: 2,
    iconId: "Kaisa",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  Lissandra: {
    id: "Lissandra",
    displayName: "Lissandra",
    tier: "rare",
    baseHp: 560, baseAd: 40, baseAp: 124,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "lissandraIceTomb", abilityCooldown: 3,
    iconId: "Lissandra",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.2941, attackSpeed: 0 },
  },
  Viktor: {
    id: "Viktor",
    displayName: "Viktor",
    tier: "epic",
    baseHp: 380, baseAd: 53, baseAp: 351,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "viktorDeathRay", abilityCooldown: 3,
    iconId: "Viktor",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.3529, attackSpeed: 0 },
  },
  Fiora: {
    id: "Fiora",
    displayName: "Fiora",
    tier: "rare",
    baseHp: 680, baseAd: 143, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "fioraRiposte", abilityCooldown: 2,
    iconId: "Fiora",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Malphite: {
    id: "Malphite",
    displayName: "Malphite",
    tier: "epic",
    region: "ixtal",
    baseHp: 1050, baseAd: 70, baseAp: 110,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "malphiteUltimate", abilityCooldown: 4,
    iconId: "Malphite",
    levelGrowth: { hp: 0.3529, ad: 0.1176, ap: 0.2353, attackSpeed: 0 },
  },

  Nidalee: {
    id: "Nidalee",
    displayName: "Nidalee",
    tier: "rare",
    region: "ixtal",
    baseHp: 600, baseAd: 120, baseAp: 0,
    range: 3, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "nidaleeJavelin", abilityCooldown: 3,
    iconId: "Nidalee",
    levelGrowth: { hp: 0.2941, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Neeko: {
    id: "Neeko",
    displayName: "Neeko",
    tier: "rare",
    region: "ixtal",
    baseHp: 560, baseAd: 45, baseAp: 195,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "neekoBounceQ", abilityCooldown: 3,
    iconId: "Neeko",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.3529, attackSpeed: 0 },
  },

  // ── Freljord ────────────────────────────────────────────────────────────────
  Tryndamere: {
    id: "Tryndamere",
    displayName: "Tryndamere",
    tier: "rare",
    region: "freljord",
    baseHp: 850, baseAd: 196, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: null, abilityCooldown: 0,
    iconId: "Tryndamere",
    levelGrowth: { hp: 0.2941, ad: 0.2353, ap: 0, attackSpeed: 0 },
    critChance: 0.30,
  },
  Ashe: {
    id: "Ashe",
    displayName: "Ashe",
    tier: "rare",
    region: "freljord",
    baseHp: 480, baseAd: 116, baseAp: 0,
    range: 4, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "asheVolley", abilityCooldown: 3,
    iconId: "Ashe",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },

  // ── Demacia ─────────────────────────────────────────────────────────────────
  Garen: {
    id: "Garen",
    displayName: "Garen",
    tier: "common",
    region: "demacia",
    baseHp: 900, baseAd: 52, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "garenJudgment", abilityCooldown: 2,
    iconId: "Garen",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  Ezreal: {
    id: "Ezreal",
    displayName: "Ezreal",
    tier: "common",
    region: "demacia",
    baseHp: 360, baseAd: 26, baseAp: 0,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "ezrealMysticShot", abilityCooldown: 1,
    iconId: "Ezreal",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  Sylas: {
    id: "Sylas",
    displayName: "Sylas",
    tier: "epic",
    region: "demacia",
    baseHp: 580, baseAd: 77, baseAp: 220,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "sylasChains", abilityCooldown: 3,
    iconId: "Sylas",
    levelGrowth: { hp: 0.2353, ad: 0.1765, ap: 0.2941, attackSpeed: 0 },
  },

  // ── Noxus ───────────────────────────────────────────────────────────────────
  Darius: {
    id: "Darius",
    displayName: "Darius",
    tier: "epic",
    region: "noxus",
    baseHp: 840, baseAd: 130, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "dariusApprehend", abilityCooldown: 2,
    iconId: "Darius",
    levelGrowth: { hp: 0.3529, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Katarina: {
    id: "Katarina",
    displayName: "Katarina",
    tier: "epic",
    region: "noxus",
    baseHp: 380, baseAd: 67, baseAp: 151,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "katarinaDeathLotus", abilityCooldown: 2,
    iconId: "Katarina",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0.2353, attackSpeed: 0 },
  },
  Vladimir: {
    id: "Vladimir",
    displayName: "Vladimir",
    tier: "rare",
    region: "noxus",
    baseHp: 700, baseAd: 55, baseAp: 126,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "vladimirTransfusion", abilityCooldown: 1,
    iconId: "Vladimir",
    levelGrowth: { hp: 0.2941, ad: 0.1176, ap: 0.2941, attackSpeed: 0 },
  },
  Draven: {
    id: "Draven",
    displayName: "Draven",
    tier: "rare",
    region: "noxus",
    baseHp: 500, baseAd: 50, baseAp: 0,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "dravenSpinning", abilityCooldown: 1,
    iconId: "Draven",
    levelGrowth: { hp: 0.2353, ad: 0.2941, ap: 0, attackSpeed: 0 },
  },

  // ── Piltover & Zaun ─────────────────────────────────────────────────────────
  Vayne: {
    id: "Vayne",
    displayName: "Vayne",
    tier: "epic",
    region: "piltover",
    baseHp: 440, baseAd: 120, baseAp: 0,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: null, abilityCooldown: 0,
    iconId: "Vayne",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Vi: {
    id: "Vi",
    displayName: "Vi",
    tier: "common",
    region: "piltover",
    baseHp: 780, baseAd: 147, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "viVaultBreaker", abilityCooldown: 3,
    iconId: "Vi",
    levelGrowth: { hp: 0.2941, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Jinx: {
    id: "Jinx",
    displayName: "Jinx",
    tier: "common",
    region: "piltover",
    baseHp: 430, baseAd: 90, baseAp: 0,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "jinxFishbones", abilityCooldown: 2,
    iconId: "Jinx",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Warwick: {
    id: "Warwick",
    displayName: "Warwick",
    tier: "common",
    region: "piltover",
    baseHp: 700, baseAd: 200, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: null, abilityCooldown: 0,
    iconId: "Warwick",
    levelGrowth: { hp: 0.2941, ad: 0.2353, ap: 0, attackSpeed: 0 },
    lifestealPercent: 0.20,
  },
  Caitlyn: {
    id: "Caitlyn",
    displayName: "Caitlyn",
    tier: "common",
    region: "piltover",
    baseHp: 450, baseAd: 79, baseAp: 0,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "caitlynPeacemaker", abilityCooldown: 3,
    iconId: "Caitlyn",
    levelGrowth: { hp: 0.2353, ad: 0.2941, ap: 0, attackSpeed: 0 },
  },
  Jayce: {
    id: "Jayce",
    displayName: "Jayce",
    tier: "rare",
    region: "piltover",
    baseHp: 580, baseAd: 110, baseAp: 0,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "jayceMercuryHammer", abilityCooldown: 0,
    iconId: "Jayce",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },

  // ── Shurima ─────────────────────────────────────────────────────────────────
  Renekton: {
    id: "Renekton",
    displayName: "Renekton",
    tier: "epic",
    region: "shurima",
    baseHp: 1100, baseAd: 238, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "renektonDominus", abilityCooldown: 3,
    iconId: "Renekton",
    levelGrowth: { hp: 0.3529, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Nasus: {
    id: "Nasus",
    displayName: "Nasus",
    tier: "rare",
    region: "shurima",
    baseHp: 950, baseAd: 80, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "nasusSiphoning", abilityCooldown: 2,
    iconId: "Nasus",
    levelGrowth: { hp: 0.2941, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Sivir: {
    id: "Sivir",
    displayName: "Sivir",
    tier: "common",
    region: "shurima",
    baseHp: 450, baseAd: 109, baseAp: 0,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "sivirRicochet", abilityCooldown: 2,
    iconId: "Sivir",
    levelGrowth: { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Xerath: {
    id: "Xerath",
    displayName: "Xerath",
    tier: "rare",
    region: "shurima",
    baseHp: 380, baseAd: 32, baseAp: 187,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "xerathArcaneBarrage", abilityCooldown: 3,
    iconId: "Xerath",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.3529, attackSpeed: 0 },
  },
  Azir: {
    id: "Azir",
    displayName: "Azir",
    tier: "epic",
    region: "shurima",
    baseHp: 560, baseAd: 66, baseAp: 422,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "azirSoldier", abilityCooldown: 5,
    iconId: "Azir",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.3529, attackSpeed: 0 },
  },

  // ── Targon ──────────────────────────────────────────────────────────────────
  Taric: {
    id: "Taric",
    displayName: "Taric",
    tier: "rare",
    region: "targon",
    baseHp: 1000, baseAd: 75, baseAp: 105,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "taricDazzle", abilityCooldown: 3,
    iconId: "Taric",
    levelGrowth: { hp: 0.5294, ad: 0.1176, ap: 0.2353, attackSpeed: 0 },
  },
  Pantheon: {
    id: "Pantheon",
    displayName: "Pantheon",
    tier: "rare",
    region: "targon",
    baseHp: 820, baseAd: 258, baseAp: 0,
    range: 1, moveSpeed: 2, baseAttackSpeed: 1,
    abilityId: "pantheonShield", abilityCooldown: 3,
    iconId: "Pantheon",
    levelGrowth: { hp: 0.2941, ad: 0.2353, ap: 0, attackSpeed: 0 },
  },
  Zoe: {
    id: "Zoe",
    displayName: "Zoe",
    tier: "epic",
    region: "targon",
    baseHp: 440, baseAd: 55, baseAp: 1244,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "zoeSleepyTrouble", abilityCooldown: 3,
    iconId: "Zoe",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.3529, attackSpeed: 0 },
  },
  Soraka: {
    id: "Soraka",
    displayName: "Soraka",
    tier: "rare",
    region: "targon",
    baseHp: 550, baseAd: 52, baseAp: 180,
    range: 3, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "sorakaWish", abilityCooldown: 2,
    iconId: "Soraka",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.2941, attackSpeed: 0 },
  },
  AurelionSol: {
    id: "AurelionSol",
    displayName: "Aurelion Sol",
    tier: "legendary",
    region: "targon",
    baseHp: 1200, baseAd: 55, baseAp: 222,
    range: 4, moveSpeed: 1, baseAttackSpeed: 1,
    abilityId: "aurelionStarfire", abilityCooldown: 3,
    iconId: "AurelionSol",
    levelGrowth: { hp: 0.3529, ad: 0.1176, ap: 0.3529, attackSpeed: 0 },
  },
  Yuumi: {
    id: "Yuumi",
    displayName: "Yuumi",
    tier: "rare",
    baseHp: 480, baseAd: 38, baseAp: 160,
    range: 1, moveSpeed: 1, baseAttackSpeed: 0,
    abilityId: "yuumiHeal", abilityCooldown: 2,
    iconId: "Yuumi",
    levelGrowth: { hp: 0.2353, ad: 0.1176, ap: 0.2941, attackSpeed: 0 },
    yuumiAura: true,
  },
};

// Default starting champions for a new run (two Common picks).
export const STARTER_CHAMPIONS: ChampionId[] = ["Garen", "Ezreal"];
